import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ResourceLayout, ResourceHero, ResourceContactCTA, DownloadButton, DownloadRow,
  StatusBlock, SignatureFooter, H2, ACCENTS, MONO, fade,
} from './_shared'

const A = ACCENTS.amber

interface Phase {
  num: number
  title: string
  desc: string
  items: string[]
}

const PHASES: Phase[] = [
  {
    num: 1,
    title: 'Limpeza',
    desc: 'Antes de otimizar, tira o lixo. Site rápido começa com banco limpo.',
    items: [
      'Audit de plugins ativos: desativar tudo que não tem uso comprovado nos últimos 90 dias',
      'Listar e remover CPTs (Custom Post Types) não utilizados',
      'Auditar /wp-content/mu-plugins/: remover scripts sem owner conhecido',
      'Auditar themes inativos: deixar só o ativo + 1 backup (twentytwentyfour)',
      'Limpar /wp-content/uploads/ de arquivos órfãos (sem referência em post_meta)',
      'Otimizar banco: wp db optimize via WP-CLI, remover post revisions, transients expirados',
    ],
  },
  {
    num: 2,
    title: 'Padronização',
    desc: 'Mesma stack em todos os sites. Sem isso, otimização não escala.',
    items: [
      'Stack unificado: LiteSpeed Server + WP Rocket OU NGINX + W3 Total Cache (escolher 1)',
      'Migrar PHP para 8.3+ (ganho de 20 a 30% em sites antigos)',
      'OPcache habilitado e dimensionado (256MB+)',
      'Object cache via Redis ou Memcached (não usar disk cache)',
      'CDN: Cloudflare proxy + page rules pra static assets (cache-control immutable)',
      'Monitoramento de uptime + alerta no Slack/Telegram (UptimeRobot free serve)',
    ],
  },
  {
    num: 3,
    title: 'Otimização',
    desc: 'Onde 90% do score vem. Faz na ordem, mede cada passo.',
    items: [
      'WebP server-side via LiteSpeed Cache ou ShortPixel (não converte só, serve com fallback)',
      'Lazy load nativo HTML (loading="lazy") + intersection observer pros above-the-fold',
      'Cache de página + cache de objeto + cache de browser (3 camadas)',
      'Minificar e combinar CSS/JS críticos. Defer/async no resto',
      'Critical CSS inline no <head> pro above-the-fold (Speed Index ~20% típico)',
      'Eliminar queries N+1: Query Monitor mostra queries duplicadas em loops',
    ],
  },
  {
    num: 4,
    title: 'Observabilidade',
    desc: 'O que você não mede você não mantém. Site fica lento de novo em 60 dias sem isso.',
    items: [
      'PageSpeed Insights API rodando diário, gravando histórico (cron + sheet)',
      'Uptime monitoring multi-região (UptimeRobot ou Pingdom)',
      'Error tracking PHP: log de fatal errors enviado pra Slack',
      'Security scans semanais: WPScan CLI + Wordfence audit',
      'Backup automático diário com retenção 7d + semanal 4 semanas (UpdraftPlus, BackWPup)',
      'Performance budget: alerta se LCP > 2.5s ou CLS > 0.1 em 3 medições seguidas',
    ],
  },
]

const TOOLS = [
  { name: 'PageSpeed Insights', url: 'https://pagespeed.web.dev', what: 'Score Core Web Vitals + sugestões. API free.' },
  { name: 'GTmetrix', url: 'https://gtmetrix.com', what: 'Waterfall, vídeo do carregamento, comparação histórica.' },
  { name: 'WP-CLI', url: 'https://wp-cli.org', what: 'CLI essencial: wp db optimize, wp transient delete-expired, wp cache flush.' },
  { name: 'Query Monitor', url: 'https://wordpress.org/plugins/query-monitor/', what: 'Plugin de debug: queries lentas, hooks, AJAX, REST.' },
  { name: 'WPScan CLI', url: 'https://wpscan.com', what: 'Audit de segurança: versões vulneráveis, usuários expostos, configs frouxas.' },
  { name: 'Cloudflare Speed Test', url: 'https://speed.cloudflare.com', what: 'TTFB real, latência por região, comparação de protocolos.' },
]

const TARGETS = [
  { metric: 'PageSpeed mobile', target: '≥ 60', why: 'Score de campanha viável. Acima de 80 é ouro.' },
  { metric: 'LCP', target: '< 2.5s', why: 'Largest Contentful Paint. Acima disso, perde ranking e conversão.' },
  { metric: 'CLS', target: '< 0.1', why: 'Cumulative Layout Shift. Mede instabilidade visual.' },
  { metric: 'INP', target: '< 200ms', why: 'Interaction to Next Paint. Substituiu o FID em 2024.' },
  { metric: 'TTFB', target: '< 600ms', why: 'Time to First Byte. Servidor lento mata score independente do resto.' },
  { metric: 'Speed Index', target: '< 3.4s', why: 'Velocidade percebida. Crítico para LP de campanha paga.' },
]

function buildMarkdown(): string {
  const lines: string[] = []
  lines.push('# Audit WordPress em 4 fases: 24 pontos')
  lines.push('')
  lines.push('Checklist completo de performance + segurança para WordPress.')
  lines.push('Fonte: matheusedson.com/recursos/wp-audit')
  lines.push('')
  for (const p of PHASES) {
    lines.push(`## Fase ${p.num}: ${p.title}`)
    lines.push('')
    lines.push(`> ${p.desc}`)
    lines.push('')
    for (const item of p.items) {
      lines.push(`- [ ] ${item}`)
    }
    lines.push('')
  }
  lines.push('## Ferramentas gratuitas')
  lines.push('')
  for (const t of TOOLS) {
    lines.push(`- **${t.name}** (${t.url}): ${t.what}`)
  }
  lines.push('')
  lines.push('## Métricas alvo (Core Web Vitals)')
  lines.push('')
  lines.push('| Métrica | Alvo | Por quê |')
  lines.push('|---------|------|---------|')
  for (const m of TARGETS) {
    lines.push(`| ${m.metric} | ${m.target} | ${m.why} |`)
  }
  lines.push('')
  lines.push('***')
  lines.push('Matheus Edson · linharesmatheus29@gmail.com')
  return lines.join('\n')
}

export default function WpAudit() {
  const [done, setDone] = useState<Set<string>>(new Set())
  const toggle = (key: string) => {
    const next = new Set(done)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setDone(next)
  }
  const totalItems = PHASES.reduce((s, p) => s + p.items.length, 0)
  const markdown = buildMarkdown()

  return (
    <ResourceLayout>
      <ResourceHero
        tag="recurso · wordpress · lento"
        title="Audit WordPress em 4 fases: 24 pontos pra ir de 47 a 89 no PageSpeed"
        desc="Checklist real que rodei em 9 sites WordPress em 2 semanas. Caso pico: 47.8 para 89 (+41.2 pts) sem trocar tema. As 4 fases têm ordem: faz primeiro, mede, passa pra próxima."
        accent={A}
        source="wp-audit"
        word="LENTO"
      />

      <StatusBlock accent={A} rows={[
        { k: 'tipo', v: 'performance · core web vitals' },
        { k: 'formato', v: 'checklist 4 fases (24 pontos) + tabela de métricas' },
        { k: 'esforço', v: '3 a 5 dias por site' },
        { k: 'valor', v: 'PageSpeed +41 pts, LCP < 2.5s, score de campanha viável' },
      ]} />

      <motion.section {...fade(0.05)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>checklist por fase</H2>
        <DownloadRow>
          <DownloadButton content={markdown} filename="wp-audit-checklist.md" accent={A} source="wp-audit" mime="text/markdown" />
        </DownloadRow>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {PHASES.map(phase => (
            <div key={phase.num} style={{
              padding: '1.25rem 1.5rem',
              background: 'linear-gradient(180deg, #111, #0d0d0d)',
              border: '1px solid #1e1e1e',
              borderLeft: `3px solid ${A.solid}`,
              borderRadius: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
                <span style={{
                  fontFamily: MONO,
                  width: 30, height: 30, borderRadius: '50%',
                  background: A.bg, color: A.text,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.82rem', fontWeight: 800,
                  border: `1px solid ${A.border}`,
                }}>{phase.num}</span>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff' }}>Fase {phase.num}: {phase.title}</h3>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '0.9rem', lineHeight: 1.55 }}>{phase.desc}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {phase.items.map((item, i) => {
                  const key = `${phase.num}-${i}`
                  const checked = done.has(key)
                  return (
                    <label key={key} style={{
                      display: 'flex', gap: '0.65rem',
                      padding: '0.55rem 0.75rem',
                      background: checked ? A.bg : 'rgba(255,255,255,0.015)',
                      border: `1px solid ${checked ? A.border : '#1e1e1e'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}>
                      <input
                        type="checkbox" checked={checked} onChange={() => toggle(key)}
                        style={{ accentColor: A.solid, cursor: 'pointer', marginTop: 3 }}
                      />
                      <span style={{ fontSize: '0.84rem', lineHeight: 1.55, color: checked ? A.text : '#e5e5e5' }}>{item}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: MONO, fontSize: '0.72rem', color: '#555', marginTop: '0.7rem', textAlign: 'right' }}>
          {'// '}{done.size}/{totalItems} concluídos
        </p>
      </motion.section>

      <motion.section {...fade(0.1)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>ferramentas gratuitas que uso</H2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#1e1e1e', border: '1px solid #1e1e1e', borderRadius: 8, overflow: 'hidden' }}>
          {TOOLS.map(t => (
            <a key={t.name} href={t.url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'block', padding: '0.85rem 1.2rem', background: '#0e0e0e', textDecoration: 'none', color: 'inherit', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#141414'}
              onMouseLeave={e => e.currentTarget.style.background = '#0e0e0e'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#fff' }}>{t.name}</span>
                <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: A.text }}>↗</span>
              </div>
              <p style={{ fontSize: '0.78rem', color: '#aaa', lineHeight: 1.55 }}>{t.what}</p>
            </a>
          ))}
        </div>
      </motion.section>

      <motion.section {...fade(0.13)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>métricas alvo (core web vitals)</H2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#1e1e1e', border: '1px solid #1e1e1e', borderRadius: 8, overflow: 'hidden' }}>
          {TARGETS.map(t => (
            <div key={t.metric} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', alignItems: 'baseline', padding: '0.85rem 1.1rem', background: '#0e0e0e' }}>
              <span style={{ fontSize: '0.86rem', fontWeight: 700, color: '#fff', minWidth: 130 }}>{t.metric}</span>
              <span style={{ fontSize: '0.92rem', color: A.text, fontFamily: MONO, fontWeight: 700, minWidth: 70 }}>{t.target}</span>
              <span style={{ fontSize: '0.76rem', color: '#aaa', lineHeight: 1.55, flex: '1 1 100%' }}>{t.why}</span>
            </div>
          ))}
        </div>
      </motion.section>

      <ResourceContactCTA accent={A} source="wp-audit" />
      <SignatureFooter accent={A} />
    </ResourceLayout>
  )
}
